/**
 * SIBChartRenderer - Renders charts from SIB (Signal Intelligence Brief) data
 * 
 * Handles the SIB chart format with series containing {x, y} data points.
 * Supports line, bar, pie, area, scatter, heatmap, gauge, radar, etc.
 * 
 * Features:
 * - Labeled axes with configurable labels
 * - Currency prefix detection and formatting ($, €, £)
 * - Percentage formatting when applicable
 */

import React from 'react'
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart as ReBarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  Label,
  ResponsiveContainer,
} from 'recharts'

interface DataPoint {
  x: string | number
  y: number
  label?: string
  source_id?: string
}

interface DataSeries {
  series_id: string
  name: string
  color?: string
  type?: 'line' | 'bar' | 'area'
  data: DataPoint[]
  unit?: string
}

interface SIBChartData {
  type: string
  series: DataSeries[]
  interactive?: boolean
  x_axis_label?: string
  y_axis_label?: string
  y_axis_unit?: string
  x_axis_unit?: string
  title?: string
  annotations?: any[]
}

interface SIBChartRendererProps {
  data: SIBChartData
  compact?: boolean
}

const SERIES_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316']

// Detect if values should use currency formatting
// Checks y_axis_label, y_axis_unit, series names, and title for hints
// PRIORITY ORDER: Explicit % > Index > Specific metrics > Currency > Default
function detectValueFormat(data: SIBChartData): { prefix: string; suffix: string; isPercent: boolean; label: string } {
  const yAxisLabel = (data.y_axis_label || '').toLowerCase()
  const unit = (data.y_axis_unit || '').toLowerCase()
  const title = (data.title || '').toLowerCase()
  
  // Also check series names for hints - series names are HIGH priority
  const seriesNames = data.series?.map(s => (s.name || '').toLowerCase()).join(' ') || ''
  
  // Combine all text sources for detection
  const allText = `${yAxisLabel} ${unit} ${title} ${seriesNames}`
  
  // ============================================================
  // PRIORITY 1: Explicit unit markers (highest priority)
  // ============================================================
  if (unit === '%' || unit === 'percent' || seriesNames.includes('%')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'Percentage (%)' }
  }
  if (unit === '$' || unit === 'usd') {
    return { prefix: '$', suffix: '', isPercent: false, label: data.y_axis_label || 'Value ($)' }
  }
  if (unit === '€' || unit === 'eur') {
    return { prefix: '€', suffix: '', isPercent: false, label: data.y_axis_label || 'Value (€)' }
  }
  if (unit === '£' || unit === 'gbp') {
    return { prefix: '£', suffix: '', isPercent: false, label: data.y_axis_label || 'Value (£)' }
  }
  
  // ============================================================
  // PRIORITY 2: Index values (no prefix/suffix needed)
  // ============================================================
  if (allText.includes('indexed') || allText.includes('(index') || 
      (allText.includes('index') && !allText.includes('indexation'))) {
    return { prefix: '', suffix: '', isPercent: false, label: data.y_axis_label || 'Index Value' }
  }
  
  // ============================================================
  // PRIORITY 3: Specific percentage metrics (override currency keywords)
  // These MUST be checked BEFORE currency because terms like 
  // "Revenue CAGR" or "Profit Concentration" are percentages, not dollars
  // ============================================================
  
  // CAGR is ALWAYS a percentage, regardless of other words
  if (allText.includes('cagr')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'CAGR (%)' }
  }
  
  // Concentration is a percentage (market share, profit share, etc.)
  if (allText.includes('concentration') || allText.includes('share of') || allText.includes('% of')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'Share (%)' }
  }
  
  // Drawdown is always a percentage
  if (allText.includes('drawdown') || allText.includes('peak-to-trough') || allText.includes('decline')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'Change (%)' }
  }
  
  // Growth rates, margins, yields are percentages
  if (allText.includes('growth rate') || allText.includes('margin') || allText.includes('yield') ||
      allText.includes('roi') || allText.includes('roa') || allText.includes('roe') ||
      allText.includes('rate of') || allText.includes('ratio')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'Rate (%)' }
  }
  
  // Generic percentage indicators
  if (allText.includes('percent') || allText.includes('pct') || 
      allText.includes('change %') || allText.includes('growth %')) {
    return { prefix: '', suffix: '%', isPercent: true, label: data.y_axis_label || 'Percentage (%)' }
  }
  
  // ============================================================
  // PRIORITY 4: Shareholder/Total return (usually indexed, not %)
  // ============================================================
  if (allText.includes('shareholder') || allText.includes('total return') || allText.includes('tsr')) {
    return { prefix: '', suffix: '', isPercent: false, label: data.y_axis_label || 'Total Return (Indexed)' }
  }
  
  // ============================================================
  // PRIORITY 5: Currency values (only if no percentage indicators found)
  // ============================================================
  if (allText.includes('dollar') || allText.includes('usd') || allText.includes('$')) {
    return { prefix: '$', suffix: '', isPercent: false, label: data.y_axis_label || 'Value ($)' }
  }
  if (allText.includes('euro') || allText.includes('eur') || allText.includes('€')) {
    return { prefix: '€', suffix: '', isPercent: false, label: data.y_axis_label || 'Value (€)' }
  }
  if (allText.includes('pound') || allText.includes('gbp') || allText.includes('sterling') || allText.includes('£')) {
    return { prefix: '£', suffix: '', isPercent: false, label: data.y_axis_label || 'Value (£)' }
  }
  
  // Revenue, profit, etc. WITHOUT percentage indicators = currency
  if (allText.includes('revenue') || allText.includes('profit') || allText.includes('earnings') ||
      allText.includes('sales') || allText.includes('income') || allText.includes('cost') ||
      allText.includes('price') || allText.includes('valuation') || allText.includes('market cap') ||
      allText.includes('budget')) {
    // But only if there's no percentage indicator
    if (!allText.includes('%') && !allText.includes('percent') && !allText.includes('cagr') &&
        !allText.includes('growth') && !allText.includes('concentration')) {
      return { prefix: '$', suffix: '', isPercent: false, label: data.y_axis_label || 'Value ($)' }
    }
  }
  
  // ============================================================
  // DEFAULT: No formatting
  // ============================================================
  return { prefix: '', suffix: '', isPercent: false, label: data.y_axis_label || '' }
}

// Format axis tick values with appropriate prefix/suffix
function createTickFormatter(format: { prefix: string; suffix: string; isPercent: boolean; label?: string }) {
  return (value: number) => {
    const formatted = formatNumber(value)
    return `${format.prefix}${formatted}${format.suffix}`
  }
}

function SIBChartRenderer({ data, compact }: SIBChartRendererProps) {
  if (!data || !Array.isArray(data.series)) {
    return <EmptyState type="unknown" />
  }

  const chartType = data.type?.toLowerCase() || 'bar'

  switch (chartType) {
    case 'line':
    case 'line_chart':
    case 'area':
    case 'area_chart':
    case 'scatter':
    case 'scatter_chart':
      return <CartesianLineChart data={data} compact={compact} />

    case 'bar':
    case 'bar_chart':
    case 'radar':
    case 'funnel':
    case 'waterfall':
    case 'candlestick':
    case 'treemap':
    case 'sunburst':
    case 'sankey':
    case 'histogram':
    case 'boxplot':
    case 'violin':
    case 'bullet':
      return <CartesianBarChart data={data} compact={compact} />

    case 'pie':
    case 'pie_chart':
      return <InteractivePieChart data={data} compact={compact} />

    case 'gauge':
      return <GaugeChart data={data} compact={compact} />

    case 'heatmap':
      return <HeatmapChart compact={compact} />

    default:
      return <CartesianBarChart data={data} compact={compact} />
  }
}

/** External HTML legend rendered below the chart — avoids Recharts SVG legend overlap */
function ExternalLegend({ items }: { items: Array<{ key: string; label: string; color: string }> }) {
  if (items.length <= 1) return null
  return (
    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 mt-3 pt-2">
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
          <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

function CartesianLineChart({ data, compact }: { data: SIBChartData; compact?: boolean }) {
  const { data: chartData, seriesMeta } = buildCartesianDataset(data.series)
  if (chartData.length === 0 || seriesMeta.length === 0) return <EmptyState type="line" />

  const height = compact ? 280 : 360
  const valueFormat = detectValueFormat(data)
  const tickFormatter = createTickFormatter(valueFormat)
  
  // Use detected label if no explicit y_axis_label provided
  const yAxisLabel = data.y_axis_label || valueFormat.label
  const hasYAxisLabel = !!yAxisLabel
  
  // Calculate margins - smaller on mobile/compact mode
  const leftMargin = compact ? (hasYAxisLabel ? 45 : 30) : (hasYAxisLabel ? 70 : 50)
  const rightMargin = compact ? 12 : 24
  const bottomMargin = data.x_axis_label ? (compact ? 40 : 55) : (compact ? 20 : 28)

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height={height}>
          <ReLineChart 
            data={chartData} 
            margin={{ top: 12, right: rightMargin, left: leftMargin, bottom: bottomMargin }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis 
              dataKey="xLabel" 
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={{ stroke: '#E5E7EB' }}
              axisLine={{ stroke: '#E5E7EB' }}
            >
              {data.x_axis_label && (
                <Label 
                  value={data.x_axis_label} 
                  position="bottom" 
                  offset={8}
                  style={{ fontSize: 12, fill: '#6B7280', fontWeight: 500 }}
                />
              )}
            </XAxis>
            <YAxis 
              tick={{ fontSize: compact ? 10 : 11, fill: '#6B7280' }}
              tickLine={{ stroke: '#E5E7EB' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={tickFormatter}
              width={compact ? 35 : 60}
            >
              {hasYAxisLabel && (
                <Label 
                  value={yAxisLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={compact ? -30 : -55}
                  style={{ fontSize: compact ? 9 : 11, fill: '#6B7280', fontWeight: 500, textAnchor: 'middle' }}
                />
              )}
            </YAxis>
            <Tooltip content={<CartesianTooltip valueFormat={valueFormat} />} />
            {seriesMeta.map(meta => (
              <Line
                key={meta.key}
                type="monotone"
                dataKey={meta.key}
                name={meta.label}
                stroke={meta.color}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </ReLineChart>
        </ResponsiveContainer>
      </div>
      <ExternalLegend items={seriesMeta} />
    </div>
  )
}

function CartesianBarChart({ data, compact }: { data: SIBChartData; compact?: boolean }) {
  const { data: chartData, seriesMeta } = buildCartesianDataset(data.series)
  if (chartData.length === 0 || seriesMeta.length === 0) return <EmptyState type="bar" />

  const valueFormat = detectValueFormat(data)
  const tickFormatter = createTickFormatter(valueFormat)
  
  // Use detected label if no explicit y_axis_label provided
  const yAxisLabel = data.y_axis_label || valueFormat.label
  const hasYAxisLabel = !!yAxisLabel

  // Detect if x-axis labels are long and need angling
  const maxLabelLen = Math.max(...chartData.map(d => String(d.xLabel || '').length), 0)
  const needsAngle = maxLabelLen > 12 || chartData.length > 6
  
  // Calculate margins — extra bottom space for angled labels and x-axis title
  const leftMargin = compact ? (hasYAxisLabel ? 45 : 30) : (hasYAxisLabel ? 70 : 50)
  const rightMargin = compact ? 12 : 24
  const angleExtra = needsAngle ? 30 : 0
  const bottomMargin = (data.x_axis_label ? (compact ? 40 : 55) : (compact ? 20 : 28)) + angleExtra
  const height = (compact ? 280 : 360) + angleExtra

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height={height}>
          <ReBarChart 
            data={chartData} 
            margin={{ top: 12, right: rightMargin, left: leftMargin, bottom: bottomMargin }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis 
              dataKey="xLabel" 
              tick={{ fontSize: 11, fill: '#6B7280', ...(needsAngle ? { angle: -25, textAnchor: 'end' } : {}) }}
              tickLine={{ stroke: '#E5E7EB' }}
              axisLine={{ stroke: '#E5E7EB' }}
              interval={0}
            >
              {data.x_axis_label && (
                <Label 
                  value={data.x_axis_label} 
                  position="bottom" 
                  offset={needsAngle ? 20 : 8}
                  style={{ fontSize: 12, fill: '#6B7280', fontWeight: 500 }}
                />
              )}
            </XAxis>
            <YAxis 
              tick={{ fontSize: compact ? 10 : 11, fill: '#6B7280' }}
              tickLine={{ stroke: '#E5E7EB' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={tickFormatter}
              width={compact ? 35 : 60}
            >
              {hasYAxisLabel && (
                <Label 
                  value={yAxisLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={compact ? -30 : -55}
                  style={{ fontSize: compact ? 9 : 11, fill: '#6B7280', fontWeight: 500, textAnchor: 'middle' }}
                />
              )}
            </YAxis>
            <Tooltip content={<CartesianTooltip valueFormat={valueFormat} />} />
            {seriesMeta.map(meta => (
              <Bar 
                key={meta.key} 
                dataKey={meta.key} 
                name={meta.label} 
                fill={meta.color}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </ReBarChart>
        </ResponsiveContainer>
      </div>
      <ExternalLegend items={seriesMeta} />
    </div>
  )
}

function InteractivePieChart({ data, compact }: { data: SIBChartData; compact?: boolean }) {
  const series = data.series?.[0]
  const slices = series?.data || []

  if (slices.length === 0) return <EmptyState type="pie" />

  const height = compact ? 260 : 300
  const valueFormat = detectValueFormat(data)
  
  // Calculate total for percentages
  const total = slices.reduce((sum, slice) => sum + (slice.y || 0), 0)

  const pieSlices = slices.map((slice, idx) => {
    const isNumeric = (s: any) => typeof s === 'number' || (typeof s === 'string' && /^\s*[\d.,]+\s*%?\s*$/.test(s))
    const candidateLabel = slice.label && !isNumeric(slice.label) ? slice.label : null
    const candidateX = typeof slice.x === 'string' && !isNumeric(slice.x) ? slice.x : null
    return {
      name: candidateLabel || candidateX || `Segment ${idx + 1}`,
      value: slice.y,
      color: SERIES_COLORS[idx % SERIES_COLORS.length],
    }
  })

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height={height}>
          <RePieChart>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const value = payload[0].value as number
                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                return (
                  <div className="rounded-lg border border-gray-200 bg-white/95 backdrop-blur-sm px-3 py-2 text-xs text-gray-800 shadow-lg dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100">
                    <div className="font-semibold text-gray-900 dark:text-white">{payload[0].name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-gray-600 dark:text-gray-400">
                        {valueFormat.prefix}{formatNumber(value)}{valueFormat.suffix}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">({percent}%)</span>
                    </div>
                  </div>
                )
              }}
            />
            <Pie
              data={pieSlices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={compact ? 40 : 60}
              outerRadius={compact ? 80 : 110}
              paddingAngle={2}
              strokeWidth={2}
              stroke="#fff"
            >
              {pieSlices.map((s, idx) => (
                <Cell key={`cell-${idx}`} fill={s.color} />
              ))}
            </Pie>
          </RePieChart>
        </ResponsiveContainer>
      </div>
      {/* External legend with names + percentages */}
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-3 px-2">
        {pieSlices.map((s, i) => {
          const pct = total > 0 ? ((s.value / total) * 100).toFixed(1) : '0'
          return (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-xs text-gray-700 dark:text-gray-300">{s.name}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">({pct}%)</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function GaugeChart({ data, compact }: { data: SIBChartData; compact?: boolean }) {
  const height = compact ? 'h-32' : 'h-48'
  const value = data.series?.[0]?.data?.[0]?.y ?? 0
  const pct = Math.min(100, Math.max(0, (value / 100) * 100))

  return (
    <div className={`${height} p-4 flex items-center justify-center`}>
      <div className="relative w-40 h-20">
        <svg viewBox="0 0 100 50" className="w-full h-full">
          <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200 dark:text-gray-700" />
          <path
            d="M 10 50 A 40 40 0 0 1 90 50"
            fill="none"
            stroke={pct > 70 ? '#10B981' : pct > 40 ? '#F59E0B' : '#EF4444'}
            strokeWidth="8"
            strokeDasharray={`${pct * 1.26} 126`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-end justify-center pb-1">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(value)}</span>
        </div>
      </div>
    </div>
  )
}

function HeatmapChart({ compact }: { compact?: boolean }) {
  const height = compact ? 'h-48' : 'h-72'
  return (
    <div className={`${height} p-4 flex items-center justify-center`}>
      <div className="text-center text-gray-500 dark:text-gray-400">
        <div className="grid grid-cols-5 gap-1 mb-2">
          {Array.from({ length: 25 }).map((_, i) => (
            <div
              key={i}
              className="w-8 h-8 rounded"
              style={{
                backgroundColor: `rgba(59, 130, 246, ${Math.random() * 0.8 + 0.2})`,
              }}
            />
          ))}
        </div>
        <p className="text-xs">Heatmap visualization</p>
      </div>
    </div>
  )
}

function EmptyState({ type }: { type: string }) {
  return (
    <div className="h-48 bg-gray-50 dark:bg-gray-800/50 rounded-lg flex items-center justify-center">
      <div className="text-center text-gray-500 dark:text-gray-400">
        <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-sm">{type} chart</p>
        <p className="text-xs">No data available</p>
      </div>
    </div>
  )
}

interface CartesianRow {
  xKey: string
  xLabel: string
  [seriesKey: string]: string | number
}

function buildCartesianDataset(series: DataSeries[] = []) {
  const map = new Map<string, CartesianRow>()
  const meta: { key: string; label: string; color: string }[] = []

  series.forEach((seriesItem, seriesIdx) => {
    if (!Array.isArray(seriesItem?.data) || seriesItem.data.length === 0) return
    const key = seriesItem.series_id || `series_${seriesIdx + 1}`
    const color = seriesItem.color || SERIES_COLORS[seriesIdx % SERIES_COLORS.length]
    meta.push({ key, label: seriesItem.name || key, color })

    seriesItem.data.forEach((point, pointIdx) => {
      if (typeof point?.y !== 'number') return
      const bucketKey = typeof point.x === 'number' ? point.x.toString() : point.x ?? `pt_${seriesIdx}_${pointIdx}`
      if (!map.has(bucketKey)) {
        map.set(bucketKey, {
          xKey: bucketKey,
          xLabel: typeof point.x === 'number' ? point.x.toString() : String(point.x ?? bucketKey),
        })
      }
      map.get(bucketKey)![key] = point.y
    })
  })

  // Sort data by x-axis value for proper chronological/numerical ordering
  const dataArray = Array.from(map.values())
  dataArray.sort((a, b) => {
    const aKey = a.xKey
    const bKey = b.xKey
    
    // Try to parse as numbers (handles years like "2005", "2010", etc.)
    const aNum = parseFloat(aKey)
    const bNum = parseFloat(bKey)
    
    // If both are valid numbers, sort numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum
    }
    
    // Try to parse as dates (handles "Jan 2005", "2005-01", etc.)
    const aDate = Date.parse(aKey)
    const bDate = Date.parse(bKey)
    if (!isNaN(aDate) && !isNaN(bDate)) {
      return aDate - bDate
    }
    
    // Fall back to string comparison for categorical data
    return String(aKey).localeCompare(String(bKey))
  })

  return {
    data: dataArray,
    seriesMeta: meta,
  }
}

function formatNumber(value: number) {
  if (Math.abs(value) >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  if (Math.abs(value) < 1 && value !== 0) return value.toPrecision(2)
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function CartesianTooltip({ active, payload, label, valueFormat }: any) {
  if (!active || !payload?.length) return null
  
  const format = valueFormat || { prefix: '', suffix: '', isPercent: false }
  
  return (
    <div className="rounded-lg border border-gray-200 bg-white/95 backdrop-blur-sm px-3 py-2 text-xs text-gray-800 shadow-lg dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100">
      <div className="font-semibold mb-1.5 text-gray-900 dark:text-white">{label}</div>
      <div className="space-y-1">
        {payload.map((item: any) => (
          <div key={item.dataKey} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
            <span className="font-medium text-gray-600 dark:text-gray-400">{item.name}:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {format.prefix}{formatNumber(Number(item.value))}{format.suffix}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SIBChartRenderer

